import { AttachmentAction } from "@/components/mailEditor/AttachmentButton";
import { forwardRemovedAndAdded } from "@/components/mailThread/forwardPayload";
import { EditorProvider } from "@/contexts/EditorContext";
import { useModaleLocation } from "@/contexts/ModaleLocationContext";
import Email from "@/domain/entities/emails/Email";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import { ContentType } from "@/domain/entities/emails/EmailToSend";
import MailDraft from "@/domain/entities/emails/MailDraft";
import MailActionType from "@/domain/enums/MailActionType";
import useGetUserSignature from "@/hooks/signature/useGetUserSignature";
import { Tone } from "@/hooks/useGenerateAnswerWithIA";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import useSendMail from "@/hooks/useSendMail";
import { useToast } from "@/hooks/utils/useToast";
import useWindowWidth from "@/hooks/utils/useWindowWidth";
import i18n from "@/i18n";
import { cn } from "@/lib/utils";
import mailStore, {
    setMailActionType,
    setMailInputFullScreen,
} from "@/stores/mailStore";
import { useSelector } from "@tanstack/react-store";
import { Editor } from "@tiptap/react";
import {
    Dispatch,
    RefObject,
    SetStateAction,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import RichTextEditor from "../mailEditor/RichTextEditor";
import { FocusTarget } from "../mailsList/MailAction";
import assembleMailContent from "./assembleMailContent";
import draftToHtml from "./draftToHtml";
import ForwardedMessage from "./ForwardedMessage";
import MailInputFooter from "./MailInputFooter";
import MailInputHeader from "./MailInputHeader";
import MailSignatureBlock from "./MailSignatureBlock";
import { dedupeRecipients } from "./recipients";
import replySubject from "./replySubject";
import restoreCidImages from "./restoreCidImages";

interface MailInputProps {
    emailToReplyToOrForward?: Email;
    closeDialog?: () => void;
    open?: boolean;
    toInputRef?: RefObject<HTMLInputElement | null>;
    isNewMessage?: boolean;
    setMailInputExpanded?: (
        mailInputExpanded: boolean,
        focusTarget?: FocusTarget,
    ) => void;
    mailInputExpanded?: boolean;
    editor: Editor;
    suggestionEditor: Editor;
    pendingSuggestion: boolean;
    setPendingSuggestion: Dispatch<SetStateAction<boolean>>;
    makeSuggestion: (action: Tone) => void;
    acceptOrRefuseSuggestion: (accept: boolean) => void;
    isLoading: boolean;
    isPendingChange: boolean;
    attachments: EmailAttachment[];
    setAttachments: Dispatch<SetStateAction<EmailAttachment[]>>;
    forwardOriginalAttachments?: EmailAttachment[];
    forwardAttachmentsReady?: boolean;
    /** Enriched body (thread copy, cid: inline images resolved to data:) used as the forwarded quote. */
    forwardBody?: string;
    /**
     * A draft prepared by the assistant: its text fills the editor once the composer has pre-filled
     * (recipients and subject of a reply stay the composer's); a new mail also takes its subject.
     */
    draft?: MailDraft;
    /**
     * In a tab of the assistant's side panel: the full composer (subject, editor, toolbar) fills
     * its tab instead of covering the screen. `closeDialog` closes the tab.
     */
    embedded?: boolean;
}

const MailInput = ({
    emailToReplyToOrForward,
    closeDialog,
    open = true,
    toInputRef,
    isNewMessage = false,
    setMailInputExpanded,
    mailInputExpanded = false,
    editor,
    suggestionEditor,
    pendingSuggestion,
    setPendingSuggestion,
    makeSuggestion,
    acceptOrRefuseSuggestion,
    isLoading,
    isPendingChange,
    attachments,
    setAttachments,
    forwardOriginalAttachments = [],
    forwardAttachmentsReady = true,
    forwardBody,
    draft,
    embedded = false,
}: MailInputProps) => {
    const mailActionType = useSelector(
        mailStore,
        (state) => state.mailActionType,
    );
    const { t } = useTranslation();
    const { toast } = useToast();
    const windowWidth = useWindowWidth();
    const isSmallScreen = windowWidth < 1024;
    const [isFullScreen, setIsFullScreen] = useState(isNewMessage || embedded);
    const [isSending, setIsSending] = useState(false);
    const { data: userProfile } = useGetUserProfile();
    const { sendMail, replyToMail, forwardMail } = useSendMail();

    const {
        userSanitizedSignature: { data: userSanitizedSignatureData },
    } = useGetUserSignature();

    // Signature and forwarded thread live OUTSIDE the editor (the editor holds
    // only the user's message) and are re-joined on send. This is what keeps a
    // bullet list from ever being created inside the signature (AB#1746).
    // signatureHtml seeds from the saved signature and can be edited per-mail.
    const [signatureHtml, setSignatureHtml] = useState("");

    const [subject, setSubject] = useState("");

    const isInputOpen = mailInputExpanded || pendingSuggestion;

    const forwardIdRef = useRef<string>(crypto.randomUUID());

    const forwardOriginalsRef = useRef<EmailAttachment[]>([]);

    // AB#1681 — Anti-double-seed guard: we (re)pre-fill the forward's original attachments only once per
    // mail, otherwise a thread re-render (mail-updated) would re-add an attachment the user just removed.
    const forwardSeededRef = useRef<string | null>(null);

    const toRecipientsState = useState<string[]>([]);
    const ccRecipientsState = useState<string[]>([]);
    const bccRecipientsState = useState<string[]>([]);

    const placeholderRegex = useMemo(
        () => /<p[^>]*id="placeholder"[^>]*>.*?<\/p>/g,
        [],
    );

    const normalizeLinksInHtml = (html: string): string => {
        if (!html) {
            return html;
        }

        return html.replace(
            /<a\s+([^>]*?\s+)?href\s*=\s*(["']?)([^\s"'<>]+)\2([^>]*)>/gi,
            (_match, beforeHref, quote, href, afterHref) => {
                const trimmedHref = href.trim();
                if (!trimmedHref) {
                    return _match;
                }

                const hasProtocol =
                    trimmedHref.startsWith("http://") ||
                    trimmedHref.startsWith("https://") ||
                    trimmedHref.startsWith("mailto:") ||
                    trimmedHref.startsWith("tel:") ||
                    trimmedHref.startsWith("#") ||
                    trimmedHref.startsWith("ftp://") ||
                    trimmedHref.startsWith("file://");

                if (!hasProtocol && /^[a-zA-Z0-9]/.test(trimmedHref)) {
                    const normalizedHref = `https://${trimmedHref}`;
                    const quoteChar = quote || '"';

                    return `<a ${beforeHref || ""}href=${quoteChar}${normalizedHref}${quoteChar}${afterHref}>`;
                }

                return _match;
            },
        );
    };

    const placeholder = useMemo(
        () =>
            `<p id="placeholder" style="color: #aaa;">${t("email.writeYourMessageHere")}</p>`,
        [t, i18n.language],
    );

    const getContentWithoutPlaceholder = () => {
        const content =
            editor?.getHTML()?.replace(placeholderRegex, "<p></p>") || "";

        return normalizeLinksInHtml(content);
    };

    const { forwardedHtml, forwardedHtmlPreview } = useMemo(() => {
        if (
            mailActionType !== MailActionType.FORWARD ||
            !emailToReplyToOrForward
        ) {
            return { forwardedHtml: "", forwardedHtmlPreview: "" };
        }

        const original = emailToReplyToOrForward;
        const formattedDate = `${original.sentDateTime.toLocaleDateString()} - ${original.sentDateTime.toLocaleTimeString()}`;

        const header = `<hr id="forward-separator" />
<p><strong>${t("email.from")}</strong>: ${original.from.email}</p>
<p><strong>${t("email.to")}</strong>: ${original.to.map((recipient) => recipient.email).join(", ")}</p>
<p><strong>${t("email.date")}</strong>: ${formattedDate}</p>
<p><strong>${t("email.subject")}</strong>: ${original.subject}</p>
<p></p>
`;

        return {
            forwardedHtml: `${header}${original.message}`,
            forwardedHtmlPreview: `${header}${forwardBody ?? original.message}`,
        };
    }, [mailActionType, emailToReplyToOrForward, forwardBody, t]);

    // Set once the user edits the quote (AB#1903); until then the original is sent as-is.
    const [editedForwardedHtml, setEditedForwardedHtml] = useState<
        string | null
    >(null);

    const subjectFormatter = (
        unFormattedSubject: string,
        type: MailActionType,
    ) => {
        if (type === MailActionType.FORWARD) {
            if (
                unFormattedSubject.toLowerCase().startsWith("fwd:") ||
                unFormattedSubject.toLowerCase().startsWith("fwd :")
            ) {
                return unFormattedSubject;
            }

            return `${t("email.fwd")} ${unFormattedSubject}`;
        }

        if (
            type === MailActionType.REPLY ||
            type === MailActionType.REPLY_ALL
        ) {
            return replySubject(unFormattedSubject, t("email.re"));
        }

        return unFormattedSubject;
    };

    const resetContent = () => {
        editor?.commands.setContent(placeholder);
        setSignatureHtml(userSanitizedSignatureData || "");
    };

    const preFillMail = (actionType?: MailActionType) => {
        if (!editor) {
            return;
        }

        const currentActionType = actionType ?? mailActionType;

        setSubject("");
        setAttachments([]);
        forwardIdRef.current = crypto.randomUUID();
        forwardOriginalsRef.current = [];
        forwardSeededRef.current = null;
        setEditedForwardedHtml(null);
        resetContent();
        toRecipientsState[1]([]);
        ccRecipientsState[1]([]);
        bccRecipientsState[1]([]);

        if (isNewMessage) {
            return;
        }

        if (!emailToReplyToOrForward) {
            throw new Error(
                "Email to reply to is required to reply or transfer",
            );
        }

        const toAllRecipients = [
            ...emailToReplyToOrForward.to.map((to) => to.email),
            emailToReplyToOrForward.from.email,
        ].filter((mappedEmail) => mappedEmail !== userProfile?.profile.email);
        const ccAllRecipients = emailToReplyToOrForward.cc
            .map((cc) => cc.email)
            .filter(
                (mappedEmail) => mappedEmail !== userProfile?.profile.email,
            );
        const bccAllRecipients = emailToReplyToOrForward.bcc
            .map((bcc) => bcc.email)
            .filter(
                (mappedEmail) => mappedEmail !== userProfile?.profile.email,
            );

        if (currentActionType === MailActionType.FORWARD) {
            setSubject(
                subjectFormatter(
                    emailToReplyToOrForward.subject,
                    currentActionType,
                ),
            );
            // Editor holds only the user's note; the forwarded thread is
            // rendered separately (forwardedHtml) and re-joined on send.
            editor.commands.setContent(placeholder);
            // AB#1681 — initial seed from the enriched copy. If the attachments are ALREADY resolved at
            // pre-fill time, mark this mail as seeded so the deferred effect won't re-seed it — otherwise
            // a user removal made between this seed and the effect would be re-injected. If not yet
            // resolved, the deferred re-seed (effect below) takes over when they arrive (lazy enrichment).
            setAttachments(forwardOriginalAttachments);
            forwardOriginalsRef.current = forwardOriginalAttachments;
            if (forwardAttachmentsReady) {
                forwardSeededRef.current =
                    emailToReplyToOrForward.internetMessageId;
            }

            return;
        }

        if (currentActionType === MailActionType.REPLY) {
            toRecipientsState[1]([emailToReplyToOrForward.from.email]);
            setSubject(
                subjectFormatter(
                    emailToReplyToOrForward.subject,
                    currentActionType,
                ),
            );

            return;
        }

        if (currentActionType === MailActionType.REPLY_ALL) {
            const { to, cc, bcc } = dedupeRecipients({
                to: toAllRecipients,
                cc: ccAllRecipients,
                bcc: bccAllRecipients,
            });
            toRecipientsState[1](to);
            ccRecipientsState[1](cc);
            bccRecipientsState[1](bcc);
            setSubject(
                subjectFormatter(
                    emailToReplyToOrForward.subject,
                    currentActionType,
                ),
            );
        }
    };

    // The assistant's draft, over the pre-fill: plain text turned into escaped paragraphs, and never
    // auto-linked, so nothing the model wrote becomes a link or an image. A reply keeps the subject
    // and recipients of the pre-fill; only a new mail takes the draft's subject.
    const applyDraft = () => {
        if (!editor || !draft) {
            return;
        }

        editor
            .chain()
            .setMeta("preventAutolink", true)
            .setContent(draftToHtml(draft.body))
            .run();
        if (isNewMessage && draft.subject !== undefined) {
            setSubject(draft.subject);
        }
    };

    useEffect(() => {
        if (isInputOpen && isSmallScreen) {
            setIsFullScreen(true);
        }
    }, [isInputOpen, isSmallScreen]);

    const { isModaleClosed } = useModaleLocation();

    // The inline reply lives inside the slide-out panel (z-modal-panel, below
    // the fixed header): SlideOutModal raises the panel above the header while
    // this is full screen. Dialog instances are portaled to body and don't
    // need it (`closeDialog` discriminates them).
    useEffect(() => {
        if (closeDialog) {
            return undefined;
        }

        setMailInputFullScreen(isFullScreen);

        return () => setMailInputFullScreen(false);
    }, [isFullScreen, closeDialog]);

    // Going back to the dashboard keeps this component mounted (routes render
    // against modaleLocation): collapse the reply so sliding the panel back
    // open shows the mails list again, not the expanded editor.
    useEffect(() => {
        if (isModaleClosed && !closeDialog) {
            if (pendingSuggestion) {
                acceptOrRefuseSuggestion(false);
            }
            setIsFullScreen(false);
            setMailInputExpanded?.(false);
        }
    }, [isModaleClosed]);

    useEffect(() => {
        if (!emailToReplyToOrForward) {
            return;
        }

        preFillMail();
        applyDraft();
    }, [mailActionType, isNewMessage, emailToReplyToOrForward, draft]);

    // A new mail opened with a draft: filled as the dialog opens (it is reset when it closes).
    useEffect(() => {
        if (isNewMessage && open) {
            applyDraft();
        }
    }, [isNewMessage, open, draft]);

    // AB#1681 — Original attachments are enriched lazily: if they arrive AFTER the forward was pre-filled,
    // we (re)seed once per mail (forwardSeededRef), without touching the text the user has typed.
    useEffect(() => {
        if (
            isNewMessage ||
            mailActionType !== MailActionType.FORWARD ||
            !emailToReplyToOrForward
        ) {
            return;
        }

        if (
            !forwardAttachmentsReady ||
            forwardSeededRef.current ===
                emailToReplyToOrForward.internetMessageId
        ) {
            return;
        }

        setAttachments((joints) => [...forwardOriginalAttachments, ...joints]);
        forwardOriginalsRef.current = forwardOriginalAttachments;
        forwardSeededRef.current = emailToReplyToOrForward.internetMessageId;
    }, [
        forwardAttachmentsReady,
        forwardOriginalAttachments,
        mailActionType,
        emailToReplyToOrForward,
        isNewMessage,
        setAttachments,
    ]);

    const editorHasContent = useMemo(() => {
        const currentContent = editor?.getHTML() || "";

        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = currentContent;
        const textContent = tempDiv.textContent || tempDiv.innerText || "";
        const hasImages = tempDiv.querySelector("img") !== null;

        return Boolean(
            currentContent &&
            !currentContent.includes('id="placeholder"') &&
            (textContent.trim() !== "" || hasImages),
        );
    }, [editor?.getHTML()]);

    const handleMaximizeToggle = () => {
        setIsFullScreen(!isFullScreen);
    };

    const closeAndReset = () => {
        if (closeDialog) {
            closeDialog();
        } else {
            setMailInputExpanded?.(false);
            setIsFullScreen(false);
        }

        if (!isNewMessage) {
            setMailActionType(MailActionType.REPLY);
            preFillMail(MailActionType.REPLY);
        } else {
            preFillMail();
        }
    };

    const handleSendMail = async () => {
        setIsSending(true);
        let isSuccess = false;

        const content = assembleMailContent({
            body: getContentWithoutPlaceholder(),
            signatureHtml,
            forwardedHtml: "",
        });

        if (isNewMessage) {
            isSuccess = await sendMail({
                id: crypto.randomUUID(),
                subject,
                content,
                toRecipients: toRecipientsState[0],
                ccRecipients: ccRecipientsState[0],
                bccRecipients: bccRecipientsState[0],
                contentType: ContentType.HTML,
                attachments,
            });
        } else if (
            mailActionType === MailActionType.FORWARD &&
            emailToReplyToOrForward
        ) {
            isSuccess = await forwardMail(
                forwardIdRef.current,
                {
                    id: crypto.randomUUID(),
                    content,
                    toRecipients: toRecipientsState[0],
                    ccRecipients: ccRecipientsState[0],
                    bccRecipients: bccRecipientsState[0],
                    contentType: ContentType.HTML,
                    subject,
                    attachments,
                },
                emailToReplyToOrForward,
                forwardRemovedAndAdded(
                    forwardOriginalsRef.current,
                    attachments,
                ),
                editedForwardedHtml === null
                    ? undefined
                    : restoreCidImages(
                          editedForwardedHtml,
                          forwardedHtmlPreview,
                          forwardedHtml,
                      ),
            );
        } else if (emailToReplyToOrForward) {
            isSuccess = await replyToMail(
                {
                    id: crypto.randomUUID(),
                    content,
                    toRecipients: toRecipientsState[0],
                    ccRecipients: ccRecipientsState[0],
                    bccRecipients: bccRecipientsState[0],
                    contentType: ContentType.HTML,
                    subject,
                    attachments,
                },
                emailToReplyToOrForward,
            );
        } else {
            throw new Error(
                "Email to reply to is required to reply or transfer",
            );
        }

        setIsSending(false);
        if (isSuccess) {
            closeAndReset();

            toast({
                title: t("email.sent"),
                variant: "success",
            });
        }
    };

    const handleAttachmentsChange = (
        attachmentsToChange: EmailAttachment[],
        action: AttachmentAction,
    ) => {
        if (action === AttachmentAction.ADD) {
            setAttachments(attachmentsToChange);
        } else if (action === AttachmentAction.REMOVE) {
            setAttachments(attachmentsToChange);
        }
    };

    const handleTextEditorFocus = () => {
        setMailInputExpanded?.(true);

        const currentContent = editor?.getHTML() || "";
        if (currentContent.includes('id="placeholder"')) {
            const contentWithoutPlaceholder =
                getContentWithoutPlaceholder() || "";
            editor
                ?.chain()
                .setContent(contentWithoutPlaceholder)
                .focus("start")
                .run();
            setTimeout(() => {
                editor?.commands.focus("start");
            }, 100);
        }
    };

    const handleTextEditorBlur = () => {
        if (!editorHasContent) {
            editor?.commands.setContent(placeholder);
        }
    };

    // Seed the per-mail signature from the saved one once it loads (and when it
    // changes in settings). Per-mail edits are reset on the next compose.
    useEffect(() => {
        setSignatureHtml(userSanitizedSignatureData || "");
    }, [userSanitizedSignatureData]);

    useEffect(() => {
        if (i18n.language) {
            const currentContent = editor?.getHTML() || "";
            const newContent = currentContent.replace(
                placeholderRegex,
                placeholder,
            );
            editor?.commands.setContent(newContent);
        }
    }, [i18n.language]);

    useEffect(() => {
        if (!open && isNewMessage) {
            preFillMail();
        }
    }, [open, isNewMessage]);

    return (
        <EditorProvider
            value={{
                editor,
                suggestionEditor,
                pendingSuggestion,
                setPendingSuggestion,
                emailToReplyTo: emailToReplyToOrForward,
            }}
        >
            <div
                className={cn(
                    "transition-opacity duration-300 ease-out",
                    embedded
                        ? "flex h-full min-h-0 w-full"
                        : [
                              !isFullScreen &&
                                  !isInputOpen &&
                                  "hidden lg:block lg:h-30",
                              isFullScreen &&
                                  "z-overlay fixed bottom-0 left-0 right-0 h-screen w-screen cursor-default bg-black/80 p-2 lg:p-15",
                          ],
                    open ? "opacity-100" : "pointer-events-none opacity-0",
                )}
                // WebKit doesn't focus clicked buttons: focus it so the blur below doesn't collapse the box.
                onMouseDown={(e) => {
                    if (e.button !== 0 || e.defaultPrevented) {
                        return;
                    }
                    const button = (e.target as HTMLElement).closest("button");
                    if (
                        button &&
                        !button.disabled &&
                        !button.closest(
                            '[contenteditable]:not([contenteditable="false"])',
                        )
                    ) {
                        e.preventDefault();
                        button.setAttribute("data-pointer-focus", "");
                        button.addEventListener(
                            "blur",
                            () => button.removeAttribute("data-pointer-focus"),
                            { once: true },
                        );
                        button.focus({ preventScroll: true });
                    }
                }}
                onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        // Ignore blur if focus moved to a Radix UI portal (like Select dropdown)
                        const relatedTarget = e.relatedTarget as HTMLElement;
                        const isRadixPortal = relatedTarget?.closest(
                            "[data-radix-popper-content-wrapper]",
                        );

                        const isMailActions = relatedTarget?.closest(
                            ".mail-actions-container",
                        );

                        if (
                            !pendingSuggestion &&
                            !isLoading &&
                            !isRadixPortal &&
                            !isMailActions
                        ) {
                            setMailInputExpanded?.(false);
                        }
                    }
                }}
            >
                <div
                    className={cn(
                        embedded
                            ? "flex h-full min-h-0 w-full flex-col bg-white px-4 py-3"
                            : [
                                  "inline-flex h-full w-full flex-col rounded-2xl bg-surface-primaryHover px-4 py-3 outline-4 outline-offset-[-4px] outline-surface-textArea/25",
                                  (isInputOpen || isFullScreen) &&
                                      "bg-white shadow-[0px_-4px_52px_0px_rgba(0,0,0,0.15)] outline-none",
                                  isFullScreen && "px-6 py-4",
                                  isInputOpen &&
                                      !isFullScreen &&
                                      "h-40 lg:h-65 xl:h-85",
                              ],
                    )}
                >
                    <MailInputHeader
                        bccRecipientsState={bccRecipientsState}
                        ccRecipientsState={ccRecipientsState}
                        closeDialog={closeDialog}
                        handleMaximizeToggle={handleMaximizeToggle}
                        hideCornerButton={embedded}
                        isExpanded={isInputOpen}
                        isFullScreen={isFullScreen}
                        setSubject={setSubject}
                        showMessageTypeSelect={!isNewMessage}
                        showSubjectInput={
                            isFullScreen || Boolean(mailActionType)
                        }
                        subject={subject}
                        toInputRef={toInputRef}
                        toRecipientsState={toRecipientsState}
                    />
                    <RichTextEditor
                        belowEditor={
                            <>
                                <MailSignatureBlock
                                    html={signatureHtml}
                                    onChange={setSignatureHtml}
                                />
                                <ForwardedMessage
                                    html={
                                        editedForwardedHtml ??
                                        forwardedHtmlPreview
                                    }
                                    onChange={setEditedForwardedHtml}
                                />
                            </>
                        }
                        className="flex-1 overflow-hidden px-4"
                        fullScreen={isFullScreen}
                        isExpanded={isInputOpen}
                        onBlur={handleTextEditorBlur}
                        onFocus={handleTextEditorFocus}
                    />
                    <MailInputFooter
                        acceptOrRefuseSuggestion={acceptOrRefuseSuggestion}
                        attachments={attachments}
                        isExpanded={isInputOpen}
                        isFullScreen={isFullScreen}
                        isLoading={isLoading}
                        isPendingChange={isPendingChange}
                        isSendButtonDisabled={
                            toRecipientsState[0].length === 0 ||
                            pendingSuggestion ||
                            isPendingChange
                        }
                        isSending={isSending}
                        makeSuggestion={makeSuggestion}
                        onAttachmentsChange={handleAttachmentsChange}
                        onSendMail={handleSendMail}
                        sendWarnings={
                            subject.trim() === ""
                                ? {
                                      id: "no-subject",
                                      message: t("email.warnings.noSubject"),
                                  }
                                : undefined
                        }
                    />
                </div>
            </div>
        </EditorProvider>
    );
};

export default MailInput;
